import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import type { WorkerDocument } from "model/WorkerDocument";
import { formatBytes } from "utils/utils";
import {
  WorkerDocumentDownload,
  WorkerDocumentCardButtonWrapper,
  WorkerDocumentCardItem,
  WorkerDocumentCardList,
  WorkerDocumentCardTitle,
  WorkerDocumentDownloadWrapper,
  WorkerDocumentFileSize,
  WorkerDocumentList,
  WorkerDocumentListItem,
  WorkerDocumentListItemTitle,
  WorkerDocumentListTitle,
  WorkerDocumentNoFiles,
} from "./workerDocument.styled";
import { toSafeDocumentUrl } from "./workerDocument.download.logic";

interface WorkerDocumentsProps {
  readonly documents: readonly WorkerDocument[];
  readonly presentation?: "standard" | "design-v2-card-list";
}

const openDocument = (downloadUrl: string): void => {
  const safeUrl = toSafeDocumentUrl({
    baseUrl: window.location.origin,
    downloadUrl,
  });
  if (safeUrl) window.open(safeUrl, "_blank", "noopener,noreferrer");
};

export const WorkerDocuments = (props: WorkerDocumentsProps): JSX.Element => {
  const { documents, presentation = "standard" } = props;
  const { t } = useTranslation();

  if (presentation === "design-v2-card-list") {
    return (
      <WorkerDocumentCardList>
        {documents.map((workerDocument) => {
          const safeUrl = toSafeDocumentUrl({
            baseUrl: window.location.origin,
            downloadUrl: workerDocument.downloadUrl,
          });
          const downloadLabel = `${t("General.Download")} (${formatBytes(
            workerDocument.fileSize,
          )})`;
          return (
            <WorkerDocumentCardItem
              key={`${workerDocument.fileName}-${workerDocument.downloadUrl}`}
            >
              <WorkerDocumentCardTitle>
                {workerDocument.fileName}
              </WorkerDocumentCardTitle>
              <WorkerDocumentCardButtonWrapper>
                <TooltipHover text={downloadLabel} arrow="down">
                  <PrimaryButton
                    aria-label={downloadLabel}
                    expandIconLabelOnHover={false}
                    iconName="v2/download"
                    size="small"
                    disabled={!safeUrl}
                    onClick={() => openDocument(workerDocument.downloadUrl)}
                  />
                </TooltipHover>
              </WorkerDocumentCardButtonWrapper>
            </WorkerDocumentCardItem>
          );
        })}
        {documents.length === 0 && (
          <WorkerDocumentNoFiles>
            {t("General.NoDocumentsUploaded")}
          </WorkerDocumentNoFiles>
        )}
      </WorkerDocumentCardList>
    );
  }

  return (
    <>
      <WorkerDocumentListTitle>
        {t("General.Documents")}
      </WorkerDocumentListTitle>
      <WorkerDocumentList>
        {documents.length > 0 &&
          documents.map((workerDocument) => {
            const safeUrl = toSafeDocumentUrl({
              baseUrl: window.location.origin,
              downloadUrl: workerDocument.downloadUrl,
            });
            return (
              <WorkerDocumentListItem
                key={`${workerDocument.fileName}-${workerDocument.downloadUrl}`}
              >
                <WorkerDocumentListItemTitle>
                  <span>{workerDocument.fileName}</span>
                </WorkerDocumentListItemTitle>
                <WorkerDocumentDownloadWrapper>
                  <WorkerDocumentDownload
                    aria-disabled={!safeUrl}
                    href={safeUrl ?? undefined}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <span>{t("General.Download")}</span>
                    <WorkerDocumentFileSize>
                      ({formatBytes(workerDocument.fileSize)})
                    </WorkerDocumentFileSize>
                    <CustomIcon
                      size="14px"
                      color={ColorV2.White}
                      name="v2/download"
                    />
                  </WorkerDocumentDownload>
                </WorkerDocumentDownloadWrapper>
              </WorkerDocumentListItem>
            );
          })}
        {documents.length === 0 && (
          <WorkerDocumentNoFiles>
            {t("General.NoDocumentsUploaded")}
          </WorkerDocumentNoFiles>
        )}
      </WorkerDocumentList>
    </>
  );
};
